import * as React from "react";
import { IIconProps, IShimmerStyles, ITextFieldStyles, Shimmer, TextField } from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { buildTestId, ComponentTestIds } from "../../helpers/testIdHelper";
import { BaseInputProps } from "./BaseInput";
import {
  FormContext,
  FormErrorsContext,
  FormGroupValidationModeContext,
  FormInputGroupLayoutContext,
  FormState,
  FormValidationMode,
  FormValidationModeContext,
  getFieldErrors,
  ValidationState,
} from "./Form";
import { buildLabelWrapperTestIds, LabelWrapper, LabelWrapperTestIds } from "./LabelWrapper";

export enum TextInputTypes {
  PASSWORD = "password",
  TEXT = "text",
}

export interface TextInputMultilineConfig {
  /**
   * Number of rows for the multiline
   */
  rows: number;

  /**
   * For multiline text fields, whether or not the field is resizable.
   * @default false
   */
  resizable?: boolean;
}

export interface TextInputProps extends BaseInputProps {
  /**
   * Label for TextInput
   */
  label?: string;
  /**
   * Option to specify the TextInput type
   */
  textType?: TextInputTypes;
  /**
   * Default text shown initally
   */
  defaultValue?: string;
  /**
   * Option to display the password
   */
  revealPassword?: boolean;
  /**
   * If revealPassword is true, aria label for the reveal password button
   */
  revealPasswordAriaLabel?: string;
  /**
   * Callback to validate the text input
   */
  validator?: (value: string) => string[] | undefined;
  /**
   * Callback when a text input is provided or the existing input is changed
   */
  onChange?: (value: string) => void;
  /**
   * Class name for the TextInput field
   * NOTE: Only takes effect when the FormInputGroupLayout is COMPACT
   */
  className?: string | undefined

  /**
   * multiline config for the text field.
   */
  multiline?: TextInputMultilineConfig,

  /**
   * Option to make the text field readonly.
   */
  readOnly?: boolean,

  /**
   * Display Shimmer when value is loading
   */
  loading?: boolean;
}

const textFieldStyles: Partial<ITextFieldStyles> = {
  root: { width: "100%" },
  fieldGroup: { height: "24px" },
};

const textAreaStyles: Partial<ITextFieldStyles> = { root: { width: "100%" } };

const shimmerStyles: Partial<IShimmerStyles> = {
  root: {
    width: "100%",
    // This rule needs to be applied to the component that is the direct child of the LabelWrapper.
    // It works because the LabelWrapper applies a 'child gap', but the implementation is a margin
    // applied to the direct child
    marginTop: "5px !important",
  },
  dataWrapper: { position: "relative" },
};

export type TextInputTestIds = ComponentTestIds & LabelWrapperTestIds;

export const buildTextInputTestIds = (testId?: string): TextInputTestIds => ({
  ...buildLabelWrapperTestIds(testId),
  component: buildTestId(testId),
});

/**
 * TextInput component render an input field where the user can supply the input for the field
 */
export const TextInput = ({
  testId,
  fieldName,
  groupName,
  label,
  required,
  tooltip,
  disabled,
  textType,
  defaultValue,
  subField,
  revealPassword,
  revealPasswordAriaLabel,
  statusInfo,
  placeholder,
  validator,
  onChange,
  className,
  multiline,
  readOnly,
  loading,
}: TextInputProps): JSX.Element => {
  const { groupName: layoutGroup, layout } = React.useContext(
    FormInputGroupLayoutContext,
  );
  const group = groupName || layoutGroup;
  const form: FormState = React.useContext(FormContext);
  if (!Object.keys(form).length) {
    throw new Error("TextInput should be used within form");
  }
  const validationMode = React.useContext(FormValidationModeContext);
  const groupValidationMode = React.useContext(FormGroupValidationModeContext);
  const formErrors = React.useContext(FormErrorsContext);
  const fieldErrors = getFieldErrors(formErrors, fieldName, group);
  const [errorMessage, setErrorMessage] = React.useState<string[]>();
  const [checkedIcon, setCheckedIcon] = React.useState<boolean>(false);

  const allErrors = [...errorMessage || [], ...fieldErrors || []];

  const textInputTestIds = buildTextInputTestIds(testId);

  React.useEffect(() => {
    form.registerFieldLabel(fieldName, label);
    form.registerFieldGroup(fieldName, group);

    if (validator || required) {
      form.registerField(fieldName, group);
    }
    if (defaultValue) {
      form.setValue(defaultValue, fieldName, group);
      fieldValidation();
    }
    return function cleanup() {
      if (validator || required) {
        form.unRegisterField(fieldName, group);
      }
    };
    // Only at mount time. Others props should not have side effect beyond their initial values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fieldValidation = () : void => {
    const errorMsg = validator ? validator(form.getValue(fieldName, group)) : undefined;
    const requiredMessage = required
      ? (!form.getValue(fieldName, group) ? [Messages.validation.requiredValidation()] : undefined)
      : undefined;
    const errors = [...requiredMessage || [], ...errorMsg || []];
    form.setFieldState(
      errors && errors?.length > 0 ? ValidationState.INVALID : ValidationState.VALID,
      fieldName,
      group,
    );
    setErrorMessage(errors);
  };

  const internalOnChange = (
    _: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string,
  ): void => {
    form.setValue(newValue, fieldName, group);
    if (validator || required) {
      fieldValidation();
      setCheckedIcon(form.getFieldState(fieldName, group) === ValidationState.VALID && newValue !== "");
    }
    onChange?.(newValue || "");
  };

  React.useEffect(() => {
    if (validationMode === FormValidationMode.ON || groupValidationMode[group] === FormValidationMode.ON) {
      if (validator || required) {
        fieldValidation();
      }
    }
    // Only if validation mode changes. Others props should not have side effect beyond their initial values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationMode, groupValidationMode]);

  const iconProps: IIconProps = {
    iconName: "CheckMark",
    style: { lineHeight: "10px", color: "#57a300" },
  };

  const selectedStyles = multiline
    ? textAreaStyles
    : textFieldStyles;

  return (
    <LabelWrapper
      errors={allErrors}
      fieldName={fieldName}
      layout={layout}
      className={className}
      label={label}
      subField={subField}
      required={required}
      tooltip={tooltip}
      statusInfo={statusInfo}
      testId={testId}
    >
      <Shimmer isDataLoaded={!loading} styles={shimmerStyles}>
        <TextField
          id={fieldName}
          data-test-id={textInputTestIds.component}
          disabled={disabled}
          onChange={internalOnChange}
          defaultValue={defaultValue}
          type={textType || TextInputTypes.TEXT}
          placeholder={placeholder}
          iconProps={checkedIcon ? iconProps : undefined}
          canRevealPassword={revealPassword}
          revealPasswordAriaLabel={revealPasswordAriaLabel}
          multiline={!!multiline}
          rows={multiline?.rows}
          resizable={!!((multiline && multiline.resizable === true))}
          styles={selectedStyles}
          readOnly={readOnly}
          borderless={readOnly}
        />
      </Shimmer>
    </LabelWrapper>
  );
};
